/** The billing fields the account carries, as `user.getMe` returns them. */
export type BillingProfile = {
    firstName?: string | null
    lastName?: string | null
    billingFirstName?: string | null
    billingLastName?: string | null
    taxNumber?: string | null
    phoneNumber?: string | null
    billingAddress?: {
        country?: string | null
        postalCode?: string | null
        city?: string | null
        street?: string | null
    } | null
}

export type BillingFormValues = {
    billingName: string
    billingPostalCode: string
    billingCity: string
    billingStreet: string
    billingTaxNumber: string
    phone: string
}

export const EMPTY_BILLING_FORM: BillingFormValues = {
    billingName: '',
    billingPostalCode: '',
    billingCity: '',
    billingStreet: '',
    billingTaxNumber: '',
    phone: '',
}

/**
 * The web form has no country field, so a saved address keeps whatever country it already had and a
 * new one is written as Hungary — the market the site sells in, and the same default the app's
 * billing form opens with.
 */
export const DEFAULT_BILLING_COUNTRY = 'Hungary'

/**
 * Family name first, the Hungarian order the app's forms and the invoice use.
 *
 * The billing name is preferred over the account name because the app lets them differ — somebody
 * invoicing under a spouse's or a company contact's name sets it there.
 */
export const resolveBillingName = (user: BillingProfile | null | undefined): string => {
    const billing = `${user?.billingLastName ?? ''} ${user?.billingFirstName ?? ''}`.trim()
    if (billing) return billing

    return `${user?.lastName ?? ''} ${user?.firstName ?? ''}`.trim()
}

/** What the checkout form opens with, so an account that has billing details never retypes them. */
export const buildBillingDefaults = (
    user: BillingProfile | null | undefined,
): BillingFormValues => ({
    billingName: resolveBillingName(user),
    billingPostalCode: user?.billingAddress?.postalCode ?? '',
    billingCity: user?.billingAddress?.city ?? '',
    billingStreet: user?.billingAddress?.street ?? '',
    billingTaxNumber: user?.taxNumber ?? '',
    phone: user?.phoneNumber ?? '',
})

/**
 * The subset the save reads. Stated separately because the checkout form's own schema makes the
 * tax number and phone optional, and a value the user never typed is `undefined` there.
 */
export type BillingAddressInput = {
    billingPostalCode: string | null
    billingCity: string | null
    billingStreet: string | null
    billingTaxNumber?: string | null
}

/**
 * What to persist back onto the account after a checkout, or null when there is nothing worth
 * saving.
 *
 * The tax number is only written when one was actually typed: clearing the company checkbox sends
 * no number, and taking that as "delete the one on file" would throw away a detail the user never
 * touched.
 */
export const toBillingProfileUpdate = (
    values: BillingAddressInput,
    user: BillingProfile | null | undefined,
): {
    billingAddress: { country: string; postalCode: string; city: string; street: string }
    taxNumber?: string
} | null => {
    const postalCode = values.billingPostalCode?.trim() ?? ''
    const city = values.billingCity?.trim() ?? ''
    const street = values.billingStreet?.trim() ?? ''

    if (!postalCode || !city || !street) return null

    const taxNumber = values.billingTaxNumber?.trim() ?? ''

    return {
        billingAddress: {
            country: user?.billingAddress?.country?.trim() || DEFAULT_BILLING_COUNTRY,
            postalCode,
            city,
            street,
        },
        ...(taxNumber && { taxNumber }),
    }
}

/**
 * The billing half of the checkout payload.
 *
 * A free booking issues no invoice, so it owes no billing details — the server says as much in
 * `requirePaidPurchaseDetails`, and the payment worker skips the address check for it too. The
 * fields are sent as `undefined` rather than as the empty strings the untouched form holds,
 * because the API's schema rejects an empty string where it accepts nothing at all.
 */
export const toCheckoutBillingFields = (
    values: BillingAddressInput & { billingName?: string | null },
    isFree: boolean,
): {
    billingName?: string
    billingPostalCode?: string
    billingCity?: string
    billingStreet?: string
    billingTaxNumber?: string
} => {
    if (isFree) return {}

    return {
        billingName: values.billingName || undefined,
        billingPostalCode: values.billingPostalCode || undefined,
        billingCity: values.billingCity || undefined,
        billingStreet: values.billingStreet || undefined,
        billingTaxNumber: values.billingTaxNumber || undefined,
    }
}

type Address = NonNullable<BillingProfile['billingAddress']>

export type CheckoutProfile = BillingProfile & {
    personalAddress?: Address | null
}

type BillingNameAndAddress = Pick<
    BillingFormValues,
    'billingName' | 'billingPostalCode' | 'billingCity' | 'billingStreet'
>

const NAME_AND_ADDRESS_FIELDS = [
    'billingName',
    'billingPostalCode',
    'billingCity',
    'billingStreet',
] as const satisfies readonly (keyof BillingNameAndAddress)[]

export const buildPersonalBilling = (
    user: CheckoutProfile | null | undefined,
): BillingNameAndAddress => ({
    billingName: `${user?.lastName ?? ''} ${user?.firstName ?? ''}`.trim(),
    billingPostalCode: user?.personalAddress?.postalCode ?? '',
    billingCity: user?.personalAddress?.city ?? '',
    billingStreet: user?.personalAddress?.street ?? '',
})

export const hasSeparateBilling = (user: CheckoutProfile | null | undefined): boolean => {
    const hasSavedBilling =
        !!user?.billingFirstName ||
        !!user?.billingLastName ||
        !!user?.billingAddress?.postalCode ||
        !!user?.billingAddress?.city ||
        !!user?.billingAddress?.street
    if (!hasSavedBilling) return false

    const saved = buildBillingDefaults(user)
    const personal = buildPersonalBilling(user)
    return NAME_AND_ADDRESS_FIELDS.some((field) => saved[field] !== personal[field])
}

export const buildCheckoutBillingDefaults = (
    user: CheckoutProfile | null | undefined,
): { values: BillingFormValues; sameAsPersonal: boolean } => {
    const sameAsPersonal = !hasSeparateBilling(user)
    const saved = buildBillingDefaults(user)
    return {
        sameAsPersonal,
        values: sameAsPersonal ? { ...saved, ...buildPersonalBilling(user) } : saved,
    }
}

export type ProfileUpdate = {
    firstName?: string
    lastName?: string
    phoneNumber?: string
    taxNumber?: string
    personalAddress?: { country: string; postalCode: string; city: string; street: string }
    billingAddress?: { country: string; postalCode: string; city: string; street: string }
}

const isSameAddress = (
    next: { postalCode: string; city: string; street: string },
    saved: Address | null | undefined,
) =>
    next.postalCode === (saved?.postalCode ?? '') &&
    next.city === (saved?.city ?? '') &&
    next.street === (saved?.street ?? '')

export const toProfileUpdate = (
    values: BillingAddressInput & {
        firstName?: string | null
        lastName?: string | null
        phone?: string | null
    },
    user: CheckoutProfile | null | undefined,
    { sameAsPersonal, includesBilling }: { sameAsPersonal: boolean; includesBilling: boolean },
): ProfileUpdate | null => {
    const update: ProfileUpdate = {}

    const firstName = values.firstName?.trim()
    const lastName = values.lastName?.trim()
    const phoneNumber = values.phone?.replace(/\s/g, '')

    if (firstName && firstName !== user?.firstName) update.firstName = firstName
    if (lastName && lastName !== user?.lastName) update.lastName = lastName
    if (phoneNumber && phoneNumber !== user?.phoneNumber) update.phoneNumber = phoneNumber

    if (includesBilling) {
        const billing = toBillingProfileUpdate(values, user)
        const target = sameAsPersonal ? 'personalAddress' : 'billingAddress'

        if (billing && !isSameAddress(billing.billingAddress, user?.[target])) {
            update[target] = {
                ...billing.billingAddress,
                country: user?.[target]?.country?.trim() || DEFAULT_BILLING_COUNTRY,
            }
        }
        if (billing?.taxNumber && billing.taxNumber !== user?.taxNumber) {
            update.taxNumber = billing.taxNumber
        }
    }

    return Object.keys(update).length ? update : null
}
